import React, { useEffect, useRef } from 'react';
import Chart, { ChartConfiguration } from 'chart.js/auto';

type AppPieChartProps = {
    data: {
        name: string;
        value: number;
        color?: string;
    }[];
    style?: React.CSSProperties;
    legend?: "top" | "right" | "bottom" | "left" | "center" | "chartArea" | boolean;
    inPieLabel?: 'valueName' | 'value' | 'name';
}

const COLORS = [
    '#0088FE',
    '#00C49F',
    '#FFBB28',
    '#FF8042',
    '#9b59b6',
    '#3498db',
    '#95a5a6',
    '#f1c40f',
    '#e74c3c',
    '#34495e'
];

const AppPieChart = (props: AppPieChartProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<Chart | null>(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        if (chartRef.current) {
            chartRef.current.destroy();
        }

        const labels = props.data.map(d => d.name);
        const dataValues = props.data.map(d => d.value);
        const backgroundColors = props.data.map((d, i) => d.color || COLORS[i % COLORS.length]);

        const config: ChartConfiguration = {
            type: 'pie',
            data: {
                labels: props.legend ? labels : undefined,
                datasets: [
                    {
                        data: dataValues,
                        backgroundColor: backgroundColors,
                        borderWidth: 1
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: typeof props.legend === 'string' ? props.legend : 'right',
                        display: props.legend ? true : false,
                    }
                }
            },
            plugins: [{
                id: 'customDataLabels',
                afterDatasetDraw(chart) {
                    const { ctx, data } = chart;
                    const meta = chart.getDatasetMeta(0);

                    ctx.save();
                    ctx.font = 'bold 12px Arial';
                    ctx.fillStyle = 'white';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';

                    ctx.shadowColor = 'rgba(0,0,0,0.8)';
                    ctx.shadowBlur = 4;

                    meta.data.forEach((element: any, index: number) => {
                        const value = data.datasets[0].data[index] as number;
                        if (value > 0) {
                            let text = '';
                            if (props.inPieLabel) {
                                if (props.inPieLabel === 'value') {
                                    text = `${value}`;
                                } else if (props.inPieLabel === 'name') {
                                    text = `${labels[index]}`;
                                } else {
                                    text = `${labels[index]}: ${value}`;
                                }
                            }
                            const position = element.tooltipPosition();
                            ctx.fillText(text, position.x, position.y);
                        }
                    });

                    ctx.shadowBlur = 0;
                    ctx.restore();
                }
            }]
        };

        chartRef.current = new Chart(canvasRef.current, config);

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [props.data, props.legend, props.inPieLabel]);

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%', ...props.style }}>
            <canvas ref={canvasRef} />
        </div>
    );
}

export default AppPieChart;